import './walletsTable.scss';

import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { memo, useContext, useEffect, useMemo, useRef } from 'react';

import { PairWalletsContext } from 'context/PairWalletsContext/PairWalletsContext';
import { useDebouncedCallback } from 'hooks/utility';
import { WalletAddressTableRow } from 'shared/components/WalletAddressTableRow';
import { useTypedSelector } from 'store';
import { RowComponent, TableHeaders, TBody } from 'tables/common';
import { EDexPairFilters } from 'types/filters';
import { IDexPair } from 'types/pairs';
import { IWalletTableRow } from 'types/tables/dex/walletTableRow';
import { FiltersPopup } from 'ui/Filters';
import { TableSelectedCaption } from 'ui/Table';
import { TableToolbar } from 'ui/Table';
import { TableWrapper } from 'ui/TableWrapper/TableWrapper';
import { EExchange } from 'web3';

import Actions from './Actions';
import { ConnectedBots } from './ConnectedBots';
import {
  ConnectedBotsHead,
  TokenBaseHead,
  TokenFeeHead,
  TokenQuoteHead,
  TotalUsdHead,
  TransactionsHead,
  WalletAddressHead,
} from './headers';
import Modals from './Modals';

interface IDexWalletsTableProps {
  showActions?: boolean;
  dexPair: IDexPair;
  singleSelect?: boolean;
  showTxCount?: boolean;
  showTotalUsd?: boolean;
}

const DexWalletsTable: React.FC<IDexWalletsTableProps> = ({
  showActions = true,
  dexPair,
  singleSelect = false,
  showTxCount = true,
  showTotalUsd = true,
}) => {
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const {
    tableRecords,
    records,
    virtualRecords,
    loading,
    hasMore,
    loadMore,
    rowSelection,
    selectedRecords,
    connectedWallets,
  } = useContext(PairWalletsContext);

  const columns = useMemo(
    () =>
      (
        [
          {
            id: 'select',
            size: 200,
            accessorFn: row => ({ address: row.walletAddressCol?.address, index: row.index }),
            header: ({ table }) => (
              <WalletAddressHead
                table={table}
                selectedWallets={(table.options.meta as any)?.selectedWallets || []}
                singleSelect={singleSelect}
              />
            ),
            cell: ({ row, getValue }) => {
              const data = getValue() as { address: string; index: number | null };

              if (!data) return <></>;

              return (
                <WalletAddressTableRow
                  getIsSelected={row.getIsSelected}
                  getIsSomeSelected={row.getIsSomeSelected}
                  getToggleSelectedHandler={row.getToggleSelectedHandler}
                  address={data.address}
                  network={dexPair.network}
                  isImported={data.index === null}
                />
              );
            },
          },
          {
            id: 'token-base',
            size: 120,
            header: () => <TokenBaseHead dexPair={dexPair} />,
            accessorFn: row => ({ value: row.tokenBaseCol?.value }),
            cell: memo(({ getValue }) => {
              const data = getValue() as { value: string };

              if (!data) return <>{'-'}</>;

              return <div style={{ textAlign: 'center' }}>{data.value}</div>;
            }),
          },
          {
            id: 'token-quote',
            size: 120,
            header: () => <TokenQuoteHead dexPair={dexPair} />,
            accessorFn: row => ({ value: row.tokenQuoteCol?.value }),
            cell: memo(({ getValue }) => {
              const data = getValue() as { value: string };

              if (!data) return <>{'-'}</>;
              return <div style={{ textAlign: 'center' }}>{data.value}</div>;
            }),
          },
          {
            id: 'token-fee',
            size: 120,
            header: () => <TokenFeeHead dexPair={dexPair} />,
            accessorFn: row => ({ value: row.tokenFeeCol?.value }),
            cell: memo(({ getValue }) => {
              const data = getValue() as { value: string };

              if (!data) return <>{'-'}</>;

              return <div style={{ textAlign: 'center' }}>{data.value}</div>;
            }),
          },
          showTotalUsd
            ? {
                id: 'total-usd',
                size: 120,
                header: () => <TotalUsdHead />,
                accessorFn: row => ({ value: row.totalUsdCol?.value }),
                cell: memo(({ getValue }) => {
                  const data = getValue() as { value: string };

                  if (!data) return <>{'-'}</>;

                  return <div style={{ textAlign: 'right' }}>{data.value}</div>;
                }),
              }
            : null,
          showTxCount
            ? {
                id: 'txs',
                size: 120,
                header: () => <TransactionsHead />,
                accessorFn: row => ({ value: row.txsCol?.transactionCount }),
                cell: memo(({ getValue }) => {
                  const data = getValue() as { value: number };

                  if (!data) return <>{'-'}</>;
                  return <div style={{ textAlign: 'right' }}>{data.value}</div>;
                }),
              }
            : null,
          isAdmin && showActions
            ? {
                id: 'connected-to-bot',
                header: ({
                  table: {
                    options: { meta },
                  },
                }) => {
                  return <ConnectedBotsHead meta={meta} dexPair={dexPair} />;
                },
                size: 160,
                cell: memo(
                  ({
                    row,
                    table: {
                      options: { meta },
                    },
                  }) => <ConnectedBots meta={meta} row={row} dexPair={dexPair} />,
                ),
              }
            : null,
        ] as ColumnDef<IWalletTableRow>[]
      ).filter(el => el !== null),
    [showTotalUsd, showTxCount, isAdmin, showActions, singleSelect, dexPair],
  );

  const table = useReactTable({
    data: tableRecords,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: singleSelect
      ? updater => {
          const newSelection = typeof updater === 'function' ? updater(rowSelection.get) : updater;

          const prevSelection = rowSelection.get;
          const newKeys = Object.keys(newSelection);

          const lastSelectedKey = newKeys.find(key => !prevSelection[key as any]);

          rowSelection.set(lastSelectedKey ? { [lastSelectedKey]: true } : {});
        }
      : rowSelection.set,
    state: {
      rowSelection: rowSelection.get,
    },
    meta: useMemo(
      () => ({
        selectedWallets: selectedRecords,
        records: records.get,
        setRecords: records.set,
      }),
      [selectedRecords, records],
    ),
  });

  const { rows } = table.getRowModel();
  const virtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: useMemo(() => rows.length, [rows]),
    overscan: 5,
    estimateSize: () => 44,
  });

  useEffect(() => {
    if (rows) {
      virtualRecords.set(virtualizer.getVirtualItems().map(({ index }) => rows[index].original));
    }

    //eslint-disable-next-line
  }, [virtualizer, rows]);

  const fetchMoreOnBottomReached = async (containerRefElement?: HTMLDivElement | null) => {
    if (containerRefElement) {
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

      //once the user has scrolled within 200px of the bottom of the table, fetch more data if we can
      if (scrollHeight - scrollTop - clientHeight < 200 && !loading && hasMore) {
        await loadMore();
      }
    }
  };

  const fetchMoreOnBottomReachedDebounced = useDebouncedCallback(fetchMoreOnBottomReached, 100);

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <Modals />
      <TableToolbar
        additional={
          isAdmin && (
            <TableSelectedCaption
              selected={selectedRecords.length}
              totalRecords={connectedWallets}
            />
          )
        }
        customFilters={
          <FiltersPopup dexPair={dexPair} exchange={EExchange.dex} type={EDexPairFilters.wallets} />
        }
        customActions={showActions ? <Actions /> : undefined}
      />
      <TableWrapper
        hasMore={hasMore}
        loading={loading}
        records={records.get}
        heightLimit={400}
        cRef={tableContainerRef}
        onScroll={e => fetchMoreOnBottomReachedDebounced(e.target as HTMLDivElement)}
        className="pair-page-wallets-table-container"
      >
        <table className="mm-common-table w-full">
          <TableHeaders
            isAllChecked={table.getIsAllRowsSelected()}
            table={table}
            update={selectedRecords}
          />
          <TBody
            paddingTop={virtualizer.getVirtualItems()?.[0]?.start || 0}
            paddingBottom={
              virtualizer.getTotalSize() -
              (virtualizer.getVirtualItems()?.[virtualizer.getVirtualItems().length - 1]?.end || 0)
            }
          >
            {virtualizer.getVirtualItems().map(virtualRow => {
              const row = rows[virtualRow.index];

              return (
                <RowComponent
                  isChecked={row.getIsSelected()}
                  row={row}
                  height={virtualRow.size}
                  key={virtualRow.index}
                />
              );
            })}
          </TBody>
        </table>
      </TableWrapper>
    </>
  );
};

export { DexWalletsTable };
